import type { CSSProperties } from 'react';
import {
  CatAttractionIcon,
  CatExploreIcon,
  CatFitnessIcon,
  CatFoodIcon,
  CatGasIcon,
  CatGolfIcon,
  CatGroceryIcon,
  CatHardwareIcon,
  CatMedicalIcon,
  CatRecreationIcon,
  CatServicesIcon,
  CatShoppingIcon,
  CatTownIcon,
  CatTravelIcon,
} from './Icons';
import { isGlyphCategory, placeInitials, placeTint } from '@/lib/placeIdentity';

const GLYPHS = {
  food: CatFoodIcon,
  fitness: CatFitnessIcon,
  grocery: CatGroceryIcon,
  medical: CatMedicalIcon,
  services: CatServicesIcon,
  shopping: CatShoppingIcon,
  home_improvement: CatHardwareIcon,
  explore: CatExploreIcon,
  gas: CatGasIcon,
  recreation: CatRecreationIcon,
  attraction: CatAttractionIcon,
  travel: CatTravelIcon,
  babcock_ranch: CatTownIcon,
  babcock_national: CatGolfIcon,
} as const;

interface PlaceAvatarProps {
  name: string;
  slug: string;
  category?: string | null;
  /** A real photograph or logo, once any exists. Takes precedence. */
  imageUrl?: string | null;
  size?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * A place's visual identity: its initials over a category glyph, on a tile
 * tinted deterministically from its slug.
 *
 * Purely decorative — `aria-hidden`, with no alt text and no title. Every
 * place name it accompanies is already rendered as visible text next to
 * it, so announcing the initials would just make screen readers say the
 * name twice.
 *
 * When `imageUrl` is present it renders that instead, which is the seam
 * for later photography or chain logos without touching any caller.
 */
export function PlaceAvatar({
  name,
  slug,
  category,
  imageUrl,
  size = 44,
  className,
  style,
}: PlaceAvatarProps) {
  const initials = placeInitials(name);
  const tint = placeTint(slug);
  const Glyph = isGlyphCategory(category) ? GLYPHS[category] : null;

  const box: CSSProperties = {
    width: size,
    height: size,
    ...style,
  };

  if (imageUrl) {
    return (
      <div className={`place-avatar place-avatar--image ${className ?? ''}`} style={box} aria-hidden>
        <img src={imageUrl} alt="" loading="lazy" />
      </div>
    );
  }

  return (
    <div
      className={`place-avatar ${className ?? ''}`}
      style={{ ...box, background: tint.bg, color: tint.fg }}
      data-tint={tint.name}
      aria-hidden
    >
      {Glyph && (
        <span className="place-avatar__glyph">
          <Glyph width={Math.round(size * 0.82)} height={Math.round(size * 0.82)} />
        </span>
      )}
      <span
        className="place-avatar__initials"
        style={{ fontSize: Math.round(size * (initials.length > 1 ? 0.4 : 0.48)) }}
      >
        {initials}
      </span>
    </div>
  );
}
